import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../../api/axios";

/* ─── Brand palette ──────────────────────────────────────────── */
const BRAND = {
  forest: "#0E3A2C", pine: "#1A5440", gold: "#C9952A", honey: "#E8C96A",
  cream: "#FDF6E3", sand: "#F1E6C9", ink: "#15281D", stone: "#4C5C46",
};

/* ─── Shared form styles (same language as Store Settings) ───── */
const LABEL = {
  display: "block", fontSize: 11, fontWeight: 700, letterSpacing: 1,
  color: "var(--mly-text-muted)", marginBottom: 8, textTransform: "uppercase",
};

const INPUT = {
  width: "100%", padding: "11px 14px", borderRadius: 10,
  border: "1.5px solid var(--mly-border)", fontSize: 14, boxSizing: "border-box", outline: "none",
};

const SECTION_TITLE = {
  fontSize: 15, fontWeight: 700, color: "var(--mly-text)",
  paddingBottom: 10, marginBottom: 20, borderBottom: "1px solid var(--mly-border-soft)",
  display: "flex", alignItems: "center", gap: 8,
};

const HINT = { fontSize: 11, color: "var(--mly-text-muted)", marginTop: 4 };

/* The loyalty RULES come from REELO_* environment variables and no endpoint
   accepts them — PUT /admin/loyalty/credentials takes connection fields only.
   They are shown as the server resolved them, and cannot be typed over. */
const READONLY_INPUT = {
  ...INPUT,
  background: "var(--mly-surface-2)", color: "var(--mly-text-muted)", cursor: "not-allowed",
};

const focusOn  = (e) => { e.target.style.borderColor = "var(--mly-accent)"; };
const focusOff = (e) => { e.target.style.borderColor = "var(--mly-border)"; };

/* ─── Chips ──────────────────────────────────────────────────── */
const KIND_CHIP = {
  bill:   { label: "Bill",   bg: "#E4F0EA",   color: BRAND.pine,  border: "#9CC7B4" },
  credit: { label: "Credit", bg: "#E9F7EF",   color: "#1E7A4B",   border: "#A6DDBE" },
  debit:  { label: "Debit",  bg: BRAND.cream, color: BRAND.gold,  border: BRAND.honey },
  lookup: { label: "Lookup", bg: "#EAF1FB",   color: "#1D5FA8",   border: "#AECBEB" },
  test:   { label: "Test",   bg: "#EFF1ED",   color: BRAND.stone, border: "#C9D1C4" },
};

const STATUS_CHIP = {
  success: { label: "Success", bg: "#E9F7EF", color: "#1E7A4B",   border: "#A6DDBE" },
  failed:  { label: "Failed",  bg: "#FDECEA", color: "#C62828",   border: "#F5B7B1" },
  skipped: { label: "Skipped", bg: "#EFF1ED", color: BRAND.stone, border: "#C9D1C4" },
};

const Chip = ({ spec, text }) => (
  <span style={{
    background: spec.bg, color: spec.color, border: `1px solid ${spec.border}`,
    fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20,
    whiteSpace: "nowrap", display: "inline-block",
  }}>
    {text || spec.label}
  </span>
);

/* ─── Field readers ───────────────────────────────────────────────────────
   /admin/loyalty/* returns the payload itself — no {status, message, data}
   envelope — and serializes camelCase throughout. `pick` survives only
   because several reads have a legitimate second source (a settings block or
   the form's own state), not to guess at a serialization. ── */

const pick = (obj, ...keys) => {
  if (!obj || typeof obj !== "object") return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};

/* GET /admin/loyalty/calls answers {calls, page, limit, total, pages} */
const listOf = (raw) => (Array.isArray(raw?.calls) ? raw.calls : Array.isArray(raw) ? raw : []);

const errMsg = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

/* ─── Formatters ─────────────────────────────────────────────── */
const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", { timeZone: "Asia/Kolkata", 
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
};

const inr = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return `₹${v.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
};

const num = (n) => {
  const v = Number(n);
  return Number.isFinite(v) ? v.toLocaleString("en-IN") : "—";
};

/* balanceAfter is "null unless Reelo reported it" — 0 is a real balance */
const showBalance = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString("en-IN") : String(v);
};

/* ─── Secret hygiene ─────────────────────────────────────────────────────
   The audit log redacts the key server-side; this is the belt to that
   braces so a key can never reach the DOM even if a payload slips one
   through (raw upstream echo, a new field name, an older backend). ── */
const SECRET_RE = /(api[-_ ]?key|apikey|authorization|auth|token|secret|password|bearer)/i;

const redact = (value, depth = 0) => {
  if (depth > 8) return value;
  if (Array.isArray(value)) return value.map((v) => redact(v, depth + 1));
  if (value && typeof value === "object") {
    const out = {};
    Object.keys(value).forEach((k) => {
      out[k] = SECRET_RE.test(k) ? "••••••••" : redact(value[k], depth + 1);
    });
    return out;
  }
  return value;
};

const pretty = (value) => {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(redact(value), null, 2);
  } catch (e) {
    return String(value);
  }
};

const maskKey = (key) => {
  const k = String(key || "").trim();
  if (!k) return "";
  if (/[•*]/.test(k)) return k;              // already masked upstream
  if (k.length <= 4) return "••••";
  return `${"•".repeat(Math.min(12, Math.max(4, k.length - 4)))}${k.slice(-4)}`;
};

/* a masked value must never be saved back over the real key */
const looksMasked = (key) => /[•*]/.test(String(key || ""));

const PRE = {
  margin: 0, padding: "12px 14px", borderRadius: 10,
  background: "#0E1A14", color: "#D7E7DE",
  fontSize: 11.5, lineHeight: 1.55, maxHeight: 320, overflow: "auto",
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  whiteSpace: "pre-wrap", wordBreak: "break-word",
};

/* ─── Small presentational pieces ────────────────────────────── */
const Pill = ({ tone, children }) => {
  const TONES = {
    live:    { bg: "#E9F7EF", color: "#1E7A4B",   border: "#A6DDBE", dot: "#22C55E" },
    down:    { bg: "#FDECEA", color: "#C62828",   border: "#F5B7B1", dot: "#EF4444" },
    off:     { bg: "#EFF1ED", color: BRAND.stone, border: "#C9D1C4", dot: "#9CA3AF" },
    pending: { bg: BRAND.cream, color: BRAND.gold, border: BRAND.honey, dot: BRAND.gold },
  };
  const t = TONES[tone] || TONES.off;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 7,
      background: t.bg, color: t.color, border: `1px solid ${t.border}`,
      fontSize: 12, fontWeight: 800, letterSpacing: 0.3,
      padding: "5px 14px", borderRadius: 20, whiteSpace: "nowrap",
    }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: t.dot, flexShrink: 0 }} />
      {children}
    </span>
  );
};

const StatTile = ({ label, value, tone }) => (
  <div style={{
    flex: "1 1 150px", minWidth: 140,
    background: tone === "bad" ? "#FDECEA" : "var(--mly-surface-2)",
    border: `1px solid ${tone === "bad" ? "#F5B7B1" : "var(--mly-border-soft)"}`,
    borderRadius: 16, padding: "14px 16px",
  }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1, color: tone === "bad" ? BRAND.stone : "var(--mly-text-soft)", textTransform: "uppercase" }}>
      {label}
    </div>
    <div style={{
      fontSize: 24, fontWeight: 800, marginTop: 4,
      color: tone === "bad" ? "#C62828" : "var(--mly-heading)",
    }}>
      {value}
    </div>
  </div>
);

const KeyValue = ({ label, children }) => (
  <div style={{ minWidth: 170 }}>
    <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 1, color: "var(--mly-text-muted)", textTransform: "uppercase" }}>
      {label}
    </div>
    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--mly-text)", marginTop: 3, wordBreak: "break-all" }}>
      {children}
    </div>
  </div>
);

const CardShell = ({ title, icon, right, children, style }) => (
  <div className="card" style={{ marginBottom: 20, borderRadius: 16, ...style }}>
    <div className="card-header d-flex justify-content-between align-items-center" style={{ flexWrap: "wrap", gap: 10 }}>
      <h5 style={{ margin: 0, display: "flex", alignItems: "center", gap: 9, fontSize: 16 }}>
        {icon}
        {title}
      </h5>
      {right}
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const btn = (variant, disabled) => {
  const base = {
    padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer", whiteSpace: "nowrap",
    opacity: disabled ? 0.6 : 1, border: "1.5px solid transparent",
  };
  if (variant === "primary") return { ...base, background: "var(--mly-heading)", color: "var(--mly-on-accent)" };
  if (variant === "gold")    return { ...base, background: BRAND.gold, color: "#fff" };
  if (variant === "ghost")   return { ...base, background: "var(--mly-surface)", color: "var(--mly-heading)", borderColor: "var(--mly-border)" };
  return { ...base, background: "var(--mly-surface-2)", color: "var(--mly-accent-2)", borderColor: BRAND.honey };
};

/* ─── Icons ──────────────────────────────────────────────────── */
const StarIcon = ({ size = 16, color = "var(--mly-heading)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const PlugIcon = ({ size = 16, color = "var(--mly-heading)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 2v6M15 2v6" />
    <path d="M6 8h12v3a6 6 0 0 1-6 6 6 6 0 0 1-6-6z" />
    <path d="M12 17v5" />
  </svg>
);

const FlaskIcon = ({ size = 16, color = "var(--mly-heading)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 2v6.5L4.6 18a2 2 0 0 0 1.7 3h11.4a2 2 0 0 0 1.7-3L14 8.5V2" />
    <line x1="8.5" y1="2" x2="15.5" y2="2" />
    <line x1="7" y1="15" x2="17" y2="15" />
  </svg>
);

const ListIcon = ({ size = 16, color = "var(--mly-heading)" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" />
    <line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
  </svg>
);

const Caret = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M9.4 18.6a1.2 1.2 0 0 1 0-1.7l4.9-4.9-4.9-4.9a1.2 1.2 0 0 1 1.7-1.7l5.7 5.7a1.2 1.2 0 0 1 0 1.7l-5.7 5.7a1.2 1.2 0 0 1-1.7 0Z" /></svg>
);

/* ─── Constants ──────────────────────────────────────────────── */
const AUTO_MS = 15000;
/* The server's own default (LoyaltyEndpoints.DefaultBaseUrl). "api.reelo.io" is
   in its LegacyBaseUrls list and is DISCARDED on resolve — offering it as the
   placeholder, the hint and the blank-field substitute told an operator to
   configure the one host the service throws away. */
const DEFAULT_BASE_URL = "https://capture.reelo.io";
const KIND_OPTIONS   = ["bill", "credit", "debit", "lookup", "test"];
const STATUS_OPTIONS = ["success", "failed", "skipped"];
const TX_COLSPAN = 11;

const EARN_MODES = [
  {
    value: "reelo",
    title: "Reelo awards the points",
    detail:
      "MilliyO pushes the delivered bill to Reelo and stops there — Reelo's own earning rules decide how many Yo Points the customer gets. MilliyO never credits points itself.",
    badge: "Recommended",
  },
  {
    value: "milliyo",
    title: "MilliyO decides the points",
    detail:
      "On delivery MilliyO calls Reelo's credit API for floor(bill × percent ÷ 100) points. Use this only when Reelo has no earning rule of its own — otherwise the customer earns twice.",
    badge: null,
  },
];

const ReeloConsole = () => {
  const mounted = useRef(true);

  /* ── settings form ── */
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [saving,          setSaving]          = useState(false);
  const [formError,       setFormError]       = useState("");

  const [reeloEnabled,       setReeloEnabled]       = useState(false);
  /* the raw key is never returned — this input holds a NEW key or nothing */
  const [reeloApiKey,        setReeloApiKey]        = useState("");
  const [hasApiKey,          setHasApiKey]          = useState(false);
  const [keyDirty,           setKeyDirty]           = useState(false);
  const [showKey,            setShowKey]            = useState(false);
  const [reeloBaseUrl,       setReeloBaseUrl]       = useState(DEFAULT_BASE_URL);
  const [reeloAutoSyncBills, setReeloAutoSyncBills] = useState(true);

  const [loyaltyEnabled,          setLoyaltyEnabled]          = useState(false);
  const [loyaltyRedeemValue,      setLoyaltyRedeemValue]      = useState("1");
  const [loyaltyMinRedeemPoints,  setLoyaltyMinRedeemPoints]  = useState("100");
  const [loyaltyMaxRedeemPercent, setLoyaltyMaxRedeemPercent] = useState("20");
  const [reeloEarnMode,           setReeloEarnMode]           = useState("reelo");
  const [loyaltyEarnPercent,      setLoyaltyEarnPercent]      = useState("2");

  /* ── status / summary ── */
  const [status,        setStatus]        = useState(null);
  const [statusError,   setStatusError]   = useState("");
  const [statusLoading, setStatusLoading] = useState(true);
  const [pinging,       setPinging]       = useState(false);
  const [summary,       setSummary]       = useState(null);

  /* ── test bench ── */
  const [benchPhone,  setBenchPhone]  = useState("");
  const [benchAmount, setBenchAmount] = useState("");
  const [benchBusy,   setBenchBusy]   = useState("");
  const [benchResult, setBenchResult] = useState(null);
  const [resyncId,    setResyncId]    = useState("");
  const [resyncBusy,  setResyncBusy]  = useState(false);

  /* ── audit log ── */
  const [rows,        setRows]        = useState([]);
  const [txLoading,   setTxLoading]   = useState(true);
  const [txError,     setTxError]     = useState("");
  /* the server's own reason for refusing the log (403/501), not a failure */
  const [txBlocked,   setTxBlocked]   = useState("");
  const [kindFilter,  setKindFilter]  = useState("");
  const [statFilter,  setStatFilter]  = useState("");
  const [phoneInput,  setPhoneInput]  = useState("");
  const [phoneQuery,  setPhoneQuery]  = useState("");
  const [page,        setPage]        = useState(1);
  const [limit,       setLimit]       = useState(25);
  const [totalCount,  setTotalCount]  = useState(null);
  const [expanded,    setExpanded]    = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => () => { mounted.current = false; }, []);

  /* ── load the resolved config once; auto-refresh must never clobber typing ──
     Loyalty no longer lives in store settings: shop.CfgStoreSettings has no
     reelo or loyalty columns, so the server resolves everything from the
     REELO_* environment and hands it back under `settings` on GET
     /admin/loyalty/status. ── */
  const loadSettings = useCallback(async () => {
    try {
      const res = await api.get("/admin/loyalty/status");
      const s = res.data?.settings;
      if (!mounted.current || !s) return;
      setReeloEnabled(!!s.reeloEnabled);
      /* the raw key is never returned, only reeloApiKeyMasked — so the input
         starts empty and keyDirty stays the one thing that triggers a write */
      setReeloApiKey("");
      setHasApiKey(!!s.hasApiKey);
      setKeyDirty(false);
      setReeloBaseUrl(s.reeloBaseUrl || DEFAULT_BASE_URL);
      setReeloAutoSyncBills(s.autoSyncBills !== false);
      setLoyaltyEnabled(!!s.loyaltyEnabled);
      /* redeemValuePaise is an INTEGER NUMBER OF PAISE */
      setLoyaltyRedeemValue(s.redeemValuePaise !== undefined && s.redeemValuePaise !== null ? String(s.redeemValuePaise / 100) : "1");
      setLoyaltyMinRedeemPoints(s.minRedeemPoints !== undefined && s.minRedeemPoints !== null ? String(s.minRedeemPoints) : "100");
      setLoyaltyMaxRedeemPercent(s.maxRedeemPercent !== undefined && s.maxRedeemPercent !== null ? String(s.maxRedeemPercent) : "20");
      setReeloEarnMode(s.reeloEarnMode === "milliyo" ? "milliyo" : "reelo");
      setLoyaltyEarnPercent(s.earnPercent !== undefined && s.earnPercent !== null ? String(s.earnPercent) : "2");
    } catch (err) {
      if (mounted.current) {
        toast.error(err?.response?.status === 403
          ? "This account cannot read the loyalty settings"
          : errMsg(err, "Failed to load loyalty settings"));
      }
    } finally {
      if (mounted.current) setLoadingSettings(false);
    }
  }, []);

  /* ── GET /admin/loyalty/status ──
     Reads the CACHED reachability reading and never forces a live call, so
     polling it does not generate the traffic it then reports. POST
     /admin/loyalty/ping is the explicit "check it now". ── */
  const loadStatus = useCallback(async (silent = false) => {
    if (!silent) setStatusLoading(true);
    try {
      const res = await api.get("/admin/loyalty/status");
      if (!mounted.current) return;
      setStatus(res.data);
      setStatusError("");
    } catch (err) {
      if (!mounted.current) return;
      setStatusError(err?.response?.status === 403
        ? "This account cannot read the Reelo status"
        : errMsg(err, "Could not read the Reelo status"));
      setStatus(null);
    } finally {
      if (mounted.current) {
        setStatusLoading(false);
        setLastRefresh(new Date());
      }
    }
  }, []);

  /* ── GET /admin/loyalty/summary ── */
  const loadSummary = useCallback(async () => {
    try {
      const res = await api.get("/admin/loyalty/summary");
      if (mounted.current) setSummary(res.data);
    } catch (err) {
      if (mounted.current) setSummary(null);
    }
  }, []);

  /* ── GET /admin/loyalty/calls ──
     storeId is deliberately never sent: the log has no store column, so the
     server answers 501 to a storeId and 403 to a store-scoped account. ── */
  const loadTransactions = useCallback(async (silent = false) => {
    if (!silent) setTxLoading(true);
    try {
      const params = { page, limit };
      if (kindFilter) params.kind = kindFilter;
      if (statFilter) params.status = statFilter;
      if (phoneQuery) params.phone = phoneQuery;
      const res = await api.get("/admin/loyalty/calls", { params });
      if (!mounted.current) return;
      setRows(listOf(res.data));
      const n = Number(pick(res.data, "total"));
      setTotalCount(Number.isFinite(n) ? n : null);
      setTxError("");
      setTxBlocked("");
    } catch (err) {
      if (!mounted.current) return;
      const httpStatus = err?.response?.status;
      const code = err?.response?.data?.code;
      /* 403 store_scope_unavailable / 501 store_filter_unavailable are the
         server refusing on purpose and explaining why — not a failure, and
         emphatically not "the API has not shipped" */
      const blocked = (httpStatus === 403 || httpStatus === 501) && !!code;
      setRows([]);
      setTotalCount(null);
      setTxBlocked(blocked ? errMsg(err, "The Reelo call log is not available to this account") : "");
      setTxError(blocked ? "" : errMsg(err, "Failed to load the audit log"));
    } finally {
      if (mounted.current) setTxLoading(false);
    }
  }, [page, limit, kindFilter, statFilter, phoneQuery]);

  /* mount */
  useEffect(() => { loadSettings(); }, [loadSettings]);
  useEffect(() => { loadStatus(false); loadSummary(); }, [loadStatus, loadSummary]);
  useEffect(() => { loadTransactions(false); }, [loadTransactions]);

  /* debounce the phone filter so typing doesn't hammer the API */
  useEffect(() => {
    const t = setTimeout(() => setPhoneQuery(phoneInput.trim()), 400);
    return () => clearTimeout(t);
  }, [phoneInput]);

  /* any filter change restarts at page 1 */
  useEffect(() => { setPage(1); }, [kindFilter, statFilter, phoneQuery, limit]);

  /* 15s auto-refresh */
  useEffect(() => {
    if (!autoRefresh) return undefined;
    const id = setInterval(() => {
      loadTransactions(true);
      loadStatus(true);
      loadSummary();
    }, AUTO_MS);
    return () => clearInterval(id);
  }, [autoRefresh, loadTransactions, loadStatus, loadSummary]);

  const refreshAll = useCallback((silent = false) => {
    loadTransactions(silent);
    loadStatus(silent);
    loadSummary();
  }, [loadTransactions, loadStatus, loadSummary]);

  /* ── save the CONNECTION (PUT /admin/loyalty/credentials) ──
     Connection fields only. The Yo Points rules and the earn mode are not
     accepted by any endpoint — see the read-only note on that section — so
     sending them would answer 400 nothing_to_save with the key still unsaved.
     Money is never sent: the service prices everything itself. ── */
  const handleSave = async () => {
    const key = reeloApiKey.trim();

    if (reeloEnabled && !key && !hasApiKey) {
      setFormError("A Reelo API key is required while Reelo is enabled");
      return;
    }
    if (reeloBaseUrl.trim() && !/^https?:\/\//i.test(reeloBaseUrl.trim())) {
      setFormError("Reelo base URL must start with http:// or https://");
      return;
    }

    setSaving(true);
    setFormError("");

    const payload = {
      reeloEnabled,
      reeloBaseUrl: reeloBaseUrl.trim() || DEFAULT_BASE_URL,
    };
    /* never write a masked placeholder back over the real key — only send
       the key when the admin actually typed a new one */
    if (keyDirty && key && !looksMasked(key)) payload.reeloApiKey = key;

    try {
      await api.put("/admin/loyalty/credentials", payload);
      toast.success("Reelo connection saved");
      setKeyDirty(false);
      refreshAll(true);
    } catch (err) {
      /* Real HTTP statuses, not a status field in a 200 body. 501 is the
         server validating the save in full and then refusing it honestly:
         there are no columns to store it in yet. */
      const httpStatus = err?.response?.status;
      const code = err?.response?.data?.code;
      const msg =
        httpStatus === 403
          ? "Only a superadmin may change the Reelo connection."
          : code === "settings_not_storable"
            ? errMsg(err, "The connection cannot be stored yet — set the REELO_* environment variables.")
            : errMsg(err, "Could not save the Reelo connection");
      setFormError(msg);
      toast.error(msg);
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  /* ── test bench ── */
  const runBench = async (action) => {
    const phone = benchPhone.trim();
    if (action !== "ping" && !phone) {
      toast.error("Enter a customer phone number first");
      return;
    }
    if (action === "push") {
      const amt = Number(benchAmount);
      if (!Number.isFinite(amt) || amt <= 0) {
        toast.error("Enter a bill amount greater than 0 to push");
        return;
      }
    }

    const CONF = {
      /* force:true on the server, so this really re-probes Reelo; the pill
         then refreshes from the cache it just filled, via refreshAll below */
      ping: { path: "/admin/loyalty/ping", label: "Test connection", body: {} },
      // TODO(cutover): no server endpoint for this yet. Nothing under
      // /admin/loyalty pushes a bill — bill pushes happen internally when an
      // order turns delivered/completed — and the console must not send money
      // to the server in any case. The Bill Amount input has no remaining use.
      push: { path: "/api/reelo/test_push", label: "Test push bill",  body: { phone, amount: Number(benchAmount) } },
      /* pull and lookup collapse into the one lookup call; only the label differs */
      pull: { path: "/admin/loyalty/lookup", label: "Test pull customer", body: { phone } },
      lookup: { path: "/admin/loyalty/lookup", label: "Lookup balance",  body: { phone } },
    }[action];

    setBenchBusy(action);
    if (action === "ping") setPinging(true);
    const startedAt = Date.now();

    try {
      const res = await api.post(CONF.path, CONF.body);
      if (!mounted.current) return;
      setBenchResult({
        label: CONF.label, ok: true, tookMs: Date.now() - startedAt,
        httpStatus: res.status, at: new Date(), payload: res.data,
      });
      toast.success(`${CONF.label} — done`);
    } catch (err) {
      if (!mounted.current) return;
      /* only the legacy push path can 404 now — the rest of /admin/loyalty
         is deployed, so a 404 here means that one call has no endpoint */
      const missing = err?.response?.status === 404;
      setBenchResult({
        label: CONF.label, ok: false, tookMs: Date.now() - startedAt,
        httpStatus: err?.response?.status || 0, at: new Date(),
        payload: err?.response?.data || {
          error: missing
            ? `${CONF.path} has no endpoint on this server (404)`
            : errMsg(err, "Request failed"),
        },
      });
      toast.error(missing ? `${CONF.label} — no endpoint on this server` : errMsg(err, `${CONF.label} failed`));
    } finally {
      if (mounted.current) {
        setBenchBusy("");
        setPinging(false);
        /* every Reelo call writes an audit row — pull it in */
        refreshAll(true);
      }
    }
  };

  const handleResync = async () => {
    const id = resyncId.trim();
    if (!id) { toast.error("Enter an order id to resync"); return; }
    setResyncBusy(true);
    const startedAt = Date.now();
    try {
      // TODO(cutover): no server endpoint for this yet — no re-push/replay
      // route exists under /admin/loyalty or anywhere else, so this whole row
      // of the bench has no server behind it and answers 404.
      const res = await api.post("/api/reelo/resync_order", { orderId: id });
      if (!mounted.current) return;
      setBenchResult({
        label: `Resync order ${id}`, ok: true, tookMs: Date.now() - startedAt,
        httpStatus: res.status, at: new Date(), payload: res.data,
      });
      toast.success("Order re-pushed to Reelo");
    } catch (err) {
      if (!mounted.current) return;
      const missing = err?.response?.status === 404;
      setBenchResult({
        label: `Resync order ${id}`, ok: false, tookMs: Date.now() - startedAt,
        httpStatus: err?.response?.status || 0, at: new Date(),
        payload: err?.response?.data || { error: errMsg(err, "Resync failed") },
      });
      toast.error(missing ? "There is no resync endpoint on this server" : errMsg(err, "Resync failed"));
    } finally {
      if (mounted.current) { setResyncBusy(false); refreshAll(true); }
    }
  };

  /* ── derived status read-outs ── */
  const statusSettings = useMemo(() => {
    if (!status) return null;
    const s = pick(status, "settings");
    return (s && typeof s === "object") ? s : status;
  }, [status]);

  /* Deliberately does NOT accept a generic `ok` — on many envelopes that
     means "the request succeeded", which would read as "Reelo is up" while
     Reelo is actually down. Only an explicit reachability flag counts;
     anything else stays "Status unknown". */
  const reachable = useMemo(() => {
    if (!status) return null;
    const v = pick(status, "reachable");
    return v === undefined ? null : !!v;
  }, [status]);

  const statusEnabled = useMemo(() => {
    const v = pick(statusSettings || {}, "reeloEnabled");
    return v === undefined ? reeloEnabled : !!v;
  }, [statusSettings, reeloEnabled]);

  /* maskKey() passes an already-masked value straight through, so running
     everything through it means a backend that leaks the raw key still can't
     put it on screen. */
  const maskedKey = useMemo(
    /* only ever the server's masked value — the form's field holds a key the
       admin has typed but not yet saved, which is not what this card reports */
    () => maskKey(pick(statusSettings || {}, "reeloApiKeyMasked")),
    [statusSettings]
  );

  const lastBillSyncAt = useMemo(() => pick(status || {}, "lastBillSyncAt"), [status]);

  const counts = useMemo(() => {
    const c = pick(status || {}, "counts");
    return (c && typeof c === "object") ? c : {};
  }, [status]);

  /* a key with no vendor id cannot produce one valid call, so the server's
     own `configured` is what decides "connected", not the enable switch */
  const configured = useMemo(() => {
    const v = pick(status || {}, "configured");
    return v === undefined ? null : !!v;
  }, [status]);

  const connectionTone = statusLoading && !status ? "pending"
    : !statusEnabled ? "off"
    : configured === false ? "off"
    : reachable === true ? "live"
    : reachable === false ? "down"
    : "pending";

  const connectionLabel = statusLoading && !status ? "Checking…"
    : !statusEnabled ? "Reelo disabled"
    : configured === false ? "Reelo not configured"
    : reachable === true ? "Connected to Reelo"
    : reachable === false ? "Reelo unreachable"
    : "Status unknown";

  const redeemPreview = useMemo(() => {
    const v = Number(loyaltyRedeemValue);
    const m = Number(loyaltyMinRedeemPoints);
    const p = Number(loyaltyMaxRedeemPercent);
    if (!Number.isFinite(v) || !Number.isFinite(m)) return "";
    const pct = Number.isFinite(p) ? p : 0;
    return `A customer can start redeeming at ${num(m)} points (worth ${inr(m * v)}) and can never take more than ${pct}% off a bill. 1 point = ${inr(v)}.`;
  }, [loyaltyRedeemValue, loyaltyMinRedeemPoints, loyaltyMaxRedeemPercent]);

  const hasNext = totalCount !== null ? page * limit < totalCount : rows.length >= limit;

  const rowKey = (r, i) => String(pick(r, "id") ?? `${pick(r, "at") || ""}-${i}`);

  const clearFilters = () => {
    setKindFilter(""); setStatFilter(""); setPhoneInput(""); setPhoneQuery("");
  };

  const filtersActive = !!(kindFilter || statFilter || phoneQuery);

  /* ── render ── */
  return (
    <div>
      <div className="container-fluid">

        {/* ── Reelo-is-the-source-of-truth banner ── */}
        <div style={{
          display: "flex", alignItems: "flex-start", gap: 12,
          background: "var(--milliyo-green-panel, linear-gradient(150deg,#0E3A2C,#1A5440))",
          color: BRAND.cream, borderRadius: 16, padding: "14px 18px", marginBottom: 20,
        }}>
          <span style={{ flexShrink: 0, marginTop: 2 }}>
            <StarIcon size={18} color={BRAND.honey} />
          </span>
          <div>
            <div style={{ fontWeight: 800, fontSize: 14, color: BRAND.honey, letterSpacing: 0.2 }}>
              Point balances are held in Reelo — MilliyO never stores them.
            </div>
            <div style={{ fontSize: 12.5, marginTop: 4, opacity: 0.9, lineHeight: 1.5 }}>
              Every balance on this page and in the app is a live read from Reelo. The table below is an
              append-only audit log of the calls MilliyO made — it is for debugging and reconciliation,
              never a balance. If Reelo is unreachable, redemption is refused and no points are granted.
            </div>
          </div>
        </div>

        {/* ══ STATUS ══ */}
        <CardShell
          title="Connection status"
          icon={<PlugIcon />}
          right={
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <Pill tone={connectionTone}>{connectionLabel}</Pill>
              <button type="button" onClick={() => runBench("ping")} disabled={pinging || benchBusy === "ping"}
                style={btn("primary", pinging || benchBusy === "ping")}>
                {pinging ? "Testing…" : "Test connection"}
              </button>
              <button type="button" onClick={() => refreshAll(false)} style={btn("ghost", false)}>
                Refresh
              </button>
            </div>
          }
        >
          {statusError && (
            <div style={{ background: "#fef2f2", color: "#dc2626", padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
              {statusError}
            </div>
          )}

          {/* the server's own reason, and when it last actually probed — this
              card reads a CACHED ping, so "Test connection" is what re-probes */}
          {status?.unavailableReason ? (
            <div style={{
              background: "var(--mly-surface-2)", border: `1px solid ${BRAND.honey}`,
              borderRadius: 12, padding: "10px 14px", marginBottom: 16,
              fontSize: 12.5, color: "var(--mly-text-soft)",
            }}>
              {status.unavailableReason}
              {status.pingCheckedAt ? ` · checked ${formatDateTime(status.pingCheckedAt)}` : ""}
              {status.pingHttpStatus ? ` · HTTP ${status.pingHttpStatus}` : ""}
            </div>
          ) : null}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginBottom: 18 }}>
            <KeyValue label="API key">
              {maskedKey ? <code>{maskedKey}</code> : <span style={{ color: "var(--mly-text-muted)" }}>not set</span>}
            </KeyValue>
            <KeyValue label="Base URL">
              {pick(statusSettings || {}, "reeloBaseUrl") || reeloBaseUrl || DEFAULT_BASE_URL}
            </KeyValue>
            <KeyValue label="Earn mode">
              {(pick(statusSettings || {}, "reeloEarnMode") || reeloEarnMode) === "milliyo"
                ? "MilliyO credits" : "Reelo awards"}
            </KeyValue>
            <KeyValue label="Loyalty in app">
              {(pick(statusSettings || {}, "loyaltyEnabled") ?? loyaltyEnabled) ? "Shown" : "Hidden"}
            </KeyValue>
            <KeyValue label="Last bill pushed">
              {lastBillSyncAt ? formatDateTime(lastBillSyncAt) : <span style={{ color: "var(--mly-text-muted)" }}>never</span>}
            </KeyValue>
            <KeyValue label="Auto-refresh">
              {autoRefresh
                ? `every 15s${lastRefresh ? ` · ${lastRefresh.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : ""}`
                : "paused"}
            </KeyValue>
          </div>

          {/* every count below is a 30-DAY WINDOW, not an all-time total */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <StatTile label="Success calls · 30d" value={num(pick(counts, "success") ?? 0)} />
            <StatTile label="Failed calls · 30d"  value={num(pick(counts, "failed") ?? 0)} tone={Number(pick(counts, "failed") || 0) > 0 ? "bad" : undefined} />
            <StatTile label="Skipped calls · 30d" value={num(pick(counts, "skipped") ?? 0)} />
            <StatTile label="Bills pushed · 30d" value={num(pick(summary || {}, "billsPushed30d") ?? 0)} />
            <StatTile label="Credits · 30d"      value={num(pick(summary || {}, "credits30d") ?? 0)} />
            <StatTile label="Debits · 30d"       value={num(pick(summary || {}, "debits30d") ?? 0)} />
            <StatTile label="Failures · 30d"     value={num(pick(summary || {}, "failures30d") ?? 0)}
              tone={Number(pick(summary || {}, "failures30d") || 0) > 0 ? "bad" : undefined} />
          </div>

          {/* lastError is an OBJECT {at, kind, httpStatus, phone, orderNumber,
              message}, so it is read field by field — printing it whole gave
              "[object Object]". A store-scoped account always gets null here. */}
          {summary?.lastError ? (
            <div style={{
              marginTop: 14, background: "#FDECEA", border: "1px solid #F5B7B1",
              borderRadius: 12, padding: "10px 14px", fontSize: 12.5, color: "#C62828",
            }}>
              <strong>Last error:</strong> {summary.lastError.message || "—"}
              <div style={{ marginTop: 3, opacity: 0.85 }}>
                {formatDateTime(summary.lastError.at)}
                {summary.lastError.kind ? ` · ${summary.lastError.kind}` : ""}
                {summary.lastError.httpStatus ? ` · HTTP ${summary.lastError.httpStatus}` : ""}
                {summary.lastError.orderNumber ? ` · ${summary.lastError.orderNumber}` : ""}
              </div>
            </div>
          ) : null}

          {summary?.detailsWithheld && summary?.detailsWithheldReason ? (
            <div style={{ ...HINT, marginTop: 10 }}>{summary.detailsWithheldReason}</div>
          ) : null}

          <div style={{ ...HINT, marginTop: 14 }}>
            No "points outstanding" figure appears here on purpose — that number lives in Reelo, not in MilliyO.
          </div>
        </CardShell>

        {/* ══ CONFIG ══ */}
        <CardShell title="Configuration" icon={<StarIcon />}>
          {loadingSettings ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <div style={{ maxWidth: 860 }}>
              {formError && (
                <div style={{ background: "#fef2f2", color: "#dc2626", padding: "10px 14px", borderRadius: 8, marginBottom: 20, fontSize: 13 }}>
                  {formError}
                </div>
              )}

              {/* ── Reelo connection ── */}
              <div style={SECTION_TITLE}>
                <PlugIcon />
                Reelo Connection
              </div>

              <div className="row" style={{ marginBottom: 16 }}>
                <div className="col-md-4">
                  <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", borderRadius: 10, border: "1.5px solid var(--mly-border)" }}>
                    <label className="switch" style={{ marginBottom: 0 }}>
                      <input type="checkbox" checked={reeloEnabled} onChange={(e) => setReeloEnabled(e.target.checked)} />
                      <span className="slider round" />
                    </label>
                    <span style={{ fontSize: 13, fontWeight: 600, color: reeloEnabled ? "var(--mly-heading)" : "var(--mly-text-muted)" }}>
                      Reelo {reeloEnabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>
                <div className="col-md-8">
                  <label style={LABEL}>
                    Reelo API Key {reeloEnabled && !hasApiKey
                      ? <span style={{ color: "#ef4444" }}>*</span>
                      : <span style={{ color: "#ffc500", fontWeight: 400 }}>(optional)</span>}
                  </label>
                  <div style={{ position: "relative" }}>
                    <input
                      type={showKey ? "text" : "password"}
                      value={reeloApiKey}
                      onChange={(e) => { setReeloApiKey(e.target.value); setKeyDirty(true); }}
                      placeholder={hasApiKey ? "A key is stored — type to replace it" : "Paste your Reelo API key"}
                      autoComplete="new-password"
                      style={{ ...INPUT, paddingRight: 74 }}
                      onFocus={focusOn}
                      onBlur={focusOff}
                    />
                    <button
                      type="button"
                      onClick={() => setShowKey((v) => !v)}
                      style={{
                        position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                        border: "none", background: "transparent", cursor: "pointer",
                        fontSize: 11, fontWeight: 700, color: "var(--mly-heading)",
                        padding: "6px 10px", letterSpacing: 0.5,
                      }}
                    >
                      {showKey ? "HIDE" : "SHOW"}
                    </button>
                  </div>
                  <div style={HINT}>
                    Write-only: the server never returns the key, only the masked form in the status card,
                    and it is redacted in every audit row. Leave this blank to keep the stored key.
                  </div>
                </div>
              </div>

              <div className="row" style={{ marginBottom: 16 }}>
                <div className="col-md-8">
                  <label style={LABEL}>Reelo Base URL</label>
                  <input type="text" value={reeloBaseUrl} onChange={(e) => setReeloBaseUrl(e.target.value)}
                    placeholder={DEFAULT_BASE_URL} style={INPUT} onFocus={focusOn} onBlur={focusOff} />
                  <div style={HINT}>Leave as {DEFAULT_BASE_URL} unless Reelo gave you a different host.</div>
                </div>
                <div className="col-md-4">
                  <label style={LABEL}>Bill sync (read-only)</label>
                  <div style={{ ...READONLY_INPUT, display: "flex", alignItems: "center", gap: 12 }}>
                    <label className="switch" style={{ marginBottom: 0 }}>
                      <input type="checkbox" checked={reeloAutoSyncBills} disabled readOnly />
                      <span className="slider round" />
                    </label>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      Auto-push delivered bills
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ ...HINT, marginBottom: 28 }}>
                With auto-push on, each order's bill goes to Reelo once when it turns delivered or completed.
                {" "}This switch comes from <code>REELO_*</code> and no endpoint accepts it — see below.
              </div>

              {/* ── Loyalty rules — READ-ONLY ──
                  TODO(cutover): no server endpoint writes these. PUT
                  /admin/loyalty/credentials accepts connection fields only (a
                  body of just these answers 400 nothing_to_save) and
                  PUT /admin/store-settings has no such columns. The values are
                  resolved from REELO_* and shown as the server reports them. */}
              <div style={SECTION_TITLE}>
                <StarIcon />
                Yo Points Rules
                <span style={{
                  background: "var(--mly-surface-2)", color: "var(--mly-text-muted)",
                  border: "1px solid var(--mly-border)", fontSize: 10, fontWeight: 800,
                  padding: "2px 9px", borderRadius: 20, letterSpacing: 0.5,
                }}>READ-ONLY</span>
              </div>

              <div style={{
                background: BRAND.cream, border: `1px solid ${BRAND.sand}`, borderRadius: 12,
                padding: "10px 14px", fontSize: 12.5, color: BRAND.stone, marginBottom: 16,
              }}>
                These rules come from the server's <code>REELO_*</code> environment and there is no endpoint
                that stores them yet, so they are shown as resolved and cannot be edited here.
              </div>

              <div className="row" style={{ marginBottom: 16 }}>
                <div className="col-md-4">
                  <label style={LABEL}>Loyalty in the app</label>
                  <div style={{ ...READONLY_INPUT, display: "flex", alignItems: "center", gap: 12 }}>
                    <label className="switch" style={{ marginBottom: 0 }}>
                      <input type="checkbox" checked={loyaltyEnabled} disabled readOnly />
                      <span className="slider round" />
                    </label>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {loyaltyEnabled ? "Shown to customers" : "Hidden"}
                    </span>
                  </div>
                  <div style={HINT}>The card only appears when Reelo also answers — an unreachable Reelo hides it anyway.</div>
                </div>
                <div className="col-md-4">
                  <label style={LABEL}>Redeem Value (₹ per point)</label>
                  <input type="number" value={loyaltyRedeemValue} disabled readOnly style={READONLY_INPUT} />
                </div>
                <div className="col-md-4">
                  <label style={LABEL}>Minimum Redeemable Points</label>
                  <input type="number" value={loyaltyMinRedeemPoints} disabled readOnly style={READONLY_INPUT} />
                </div>
              </div>

              <div className="row" style={{ marginBottom: 8 }}>
                <div className="col-md-4">
                  <label style={LABEL}>Max Redeem (% of bill)</label>
                  <input type="number" value={loyaltyMaxRedeemPercent} disabled readOnly style={READONLY_INPUT} />
                </div>
              </div>
              <div style={{
                background: BRAND.cream, border: `1px solid ${BRAND.sand}`, borderRadius: 12,
                padding: "10px 14px", fontSize: 12.5, color: BRAND.stone, marginBottom: 28,
              }}>
                {redeemPreview}
              </div>

              {/* ── Earn mode — READ-ONLY, same reason as the rules above ── */}
              <div style={SECTION_TITLE}>
                <FlaskIcon />
                Who Awards The Points
                <span style={{
                  background: "var(--mly-surface-2)", color: "var(--mly-text-muted)",
                  border: "1px solid var(--mly-border)", fontSize: 10, fontWeight: 800,
                  padding: "2px 9px", borderRadius: 20, letterSpacing: 0.5,
                }}>READ-ONLY</span>
              </div>

              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 14 }}>
                {EARN_MODES.map((m) => {
                  const active = reeloEarnMode === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      disabled
                      style={{
                        flex: "1 1 320px", minWidth: 280, textAlign: "left",
                        border: `1.5px solid ${active ? "var(--mly-heading)" : "var(--mly-border)"}`,
                        background: active ? "var(--mly-accent-soft)" : "var(--mly-surface)",
                        borderRadius: 16, padding: "14px 16px", cursor: "not-allowed",
                        opacity: active ? 1 : 0.65,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 6 }}>
                        <span style={{
                          width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                          border: `2px solid ${active ? "var(--mly-heading)" : "var(--mly-border)"}`,
                          background: active ? "var(--mly-heading)" : "var(--mly-surface)",
                          boxShadow: active ? "inset 0 0 0 3px var(--mly-surface)" : "none",
                        }} />
                        <span style={{ fontSize: 13.5, fontWeight: 800, color: "var(--mly-heading)" }}>{m.title}</span>
                        {m.badge && (
                          <span style={{
                            background: BRAND.cream, color: BRAND.gold, border: `1px solid ${BRAND.honey}`,
                            fontSize: 10, fontWeight: 800, padding: "2px 9px", borderRadius: 20,
                          }}>{m.badge}</span>
                        )}
                      </div>
                      <div style={{ fontSize: 12.5, color: "var(--mly-text-soft)", lineHeight: 1.55 }}>{m.detail}</div>
                    </button>
                  );
                })}
              </div>

              <div className="row" style={{ marginBottom: 8 }}>
                <div className="col-md-4">
                  <label style={{ ...LABEL, color: reeloEarnMode === "milliyo" ? "var(--mly-text-muted)" : "var(--mly-border)" }}>
                    Earn Percent (MilliyO mode)
                  </label>
                  <input
                    type="number"
                    value={loyaltyEarnPercent}
                    disabled
                    readOnly
                    style={READONLY_INPUT}
                  />
                </div>
                <div className="col-md-8" style={{ display: "flex", alignItems: "flex-end" }}>
                  <div style={{ fontSize: 12.5, color: "var(--mly-text-soft)", lineHeight: 1.55, paddingBottom: 4 }}>
                    {reeloEarnMode === "milliyo"
                      ? <>A ₹1,000 bill credits <strong>{Math.floor(1000 * (Number(loyaltyEarnPercent) || 0) / 100)}</strong> points through Reelo's credit API. Make sure Reelo's own earning rule is switched off, or the customer earns twice.</>
                      : <>This field is inactive because Reelo is awarding the points from the pushed bill. MilliyO never credits in this mode — the two paths never both run.</>}
                  </div>
                </div>
              </div>

              <div style={{ ...HINT, marginTop: 22 }}>
                Saving writes the connection fields only — enable switch, API key and base URL. Superadmin only.
                {status?.settingsSource ? ` Settings source: ${status.settingsSource}.` : ""}
              </div>

              <div style={{ display: "flex", gap: 12, marginTop: 10 }}>
                <button onClick={handleSave} disabled={saving} style={{ ...btn("primary", saving), padding: "11px 40px", fontSize: 14 }}>
                  {saving ? "Saving..." : "Save Reelo Connection"}
                </button>
                <Link to="/admin/store/settings" style={{ ...btn("ghost", false), textDecoration: "none", display: "inline-flex", alignItems: "center" }}>
                  Store Settings
                </Link>
              </div>
            </div>
          )}
        </CardShell>

        {/* ══ TEST BENCH ══ */}
        <CardShell
          title="Test bench"
          icon={<FlaskIcon />}
          right={<span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>every call here writes an audit row</span>}
        >
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 14 }}>
            <div style={{ flex: "1 1 220px", minWidth: 200 }}>
              <label style={LABEL}>Customer Phone</label>
              <input type="text" value={benchPhone} onChange={(e) => setBenchPhone(e.target.value)}
                placeholder="e.g. 9876543210" style={INPUT} onFocus={focusOn} onBlur={focusOff} />
            </div>
            <div style={{ flex: "0 1 180px", minWidth: 150 }}>
              <label style={LABEL}>Bill Amount (₹)</label>
              <input type="number" min="0" step="1" value={benchAmount} onChange={(e) => setBenchAmount(e.target.value)}
                placeholder="e.g. 750" style={INPUT} onFocus={focusOn} onBlur={focusOff} />
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", paddingBottom: 1 }}>
              <button type="button" onClick={() => runBench("push")} disabled={!!benchBusy} style={btn("primary", !!benchBusy)}>
                {benchBusy === "push" ? "Pushing…" : "Test push bill"}
              </button>
              <button type="button" onClick={() => runBench("pull")} disabled={!!benchBusy} style={btn("gold", !!benchBusy)}>
                {benchBusy === "pull" ? "Pulling…" : "Test pull customer"}
              </button>
              <button type="button" onClick={() => runBench("lookup")} disabled={!!benchBusy} style={btn("soft", !!benchBusy)}>
                {benchBusy === "lookup" ? "Looking up…" : "Lookup balance"}
              </button>
            </div>
          </div>

          <div style={{
            display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end",
            borderTop: "1px solid var(--mly-border-soft)", paddingTop: 14, marginBottom: 16,
          }}>
            <div style={{ flex: "1 1 260px", minWidth: 220 }}>
              <label style={LABEL}>Resync An Order</label>
              <input type="text" value={resyncId} onChange={(e) => setResyncId(e.target.value)}
                placeholder="Order _id — re-pushes its bill to Reelo" style={INPUT} onFocus={focusOn} onBlur={focusOff} />
            </div>
            <button type="button" onClick={handleResync} disabled={resyncBusy} style={btn("ghost", resyncBusy)}>
              {resyncBusy ? "Resyncing…" : "Resync order"}
            </button>
          </div>

          {benchResult ? (
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: "var(--mly-heading)" }}>{benchResult.label}</span>
                <Chip spec={benchResult.ok ? STATUS_CHIP.success : STATUS_CHIP.failed}
                  text={benchResult.ok ? "OK" : "Failed"} />
                <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
                  HTTP {benchResult.httpStatus || "—"} · {benchResult.tookMs} ms · {formatDateTime(benchResult.at)}
                </span>
              </div>
              <pre style={PRE}>{pretty(benchResult.payload)}</pre>
              <div style={HINT}>
                Raw response, straight from the API. Balances shown here are Reelo's, read live at the moment you clicked.
              </div>
            </div>
          ) : (
            <div style={{
              background: "var(--mly-surface-2)", border: `1px dashed ${BRAND.honey}`, borderRadius: 12,
              padding: "22px 16px", textAlign: "center", fontSize: 13, color: "var(--mly-text-soft)",
            }}>
              Run a test above to see the raw Reelo response here.
            </div>
          )}
        </CardShell>

        {/* ══ AUDIT LOG ══ */}
        <CardShell
          title="Reelo audit log"
          icon={<ListIcon />}
          right={
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <button type="button" onClick={() => setAutoRefresh((v) => !v)} style={btn(autoRefresh ? "soft" : "ghost", false)}>
                {autoRefresh ? "Auto-refresh ON · 15s" : "Auto-refresh OFF"}
              </button>
              <button type="button" onClick={() => loadTransactions(false)} style={btn("ghost", false)}>Reload</button>
            </div>
          }
        >
          {/* filters */}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 16 }}>
            <div style={{ flex: "0 1 170px", minWidth: 150 }}>
              <label style={LABEL}>Kind</label>
              <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
                style={{ ...INPUT, cursor: "pointer" }}>
                <option value="">All kinds</option>
                {KIND_OPTIONS.map((k) => (
                  <option key={k} value={k}>{KIND_CHIP[k].label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: "0 1 170px", minWidth: 150 }}>
              <label style={LABEL}>Status</label>
              <select value={statFilter} onChange={(e) => setStatFilter(e.target.value)}
                style={{ ...INPUT, cursor: "pointer" }}>
                <option value="">All statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{STATUS_CHIP[s].label}</option>
                ))}
              </select>
            </div>
            <div style={{ flex: "1 1 200px", minWidth: 170 }}>
              <label style={LABEL}>Phone</label>
              <input type="text" value={phoneInput} onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="Filter by phone" style={INPUT} onFocus={focusOn} onBlur={focusOff} />
            </div>
            <div style={{ flex: "0 1 120px", minWidth: 110 }}>
              <label style={LABEL}>Per page</label>
              <select value={limit} onChange={(e) => setLimit(Number(e.target.value))}
                style={{ ...INPUT, cursor: "pointer" }}>
                {[15, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            {filtersActive && (
              <button type="button" onClick={clearFilters} style={{ ...btn("ghost", false), marginBottom: 1 }}>
                Clear filters
              </button>
            )}
          </div>

          {txError && (
            <div style={{ background: "#fef2f2", color: "#dc2626", padding: "10px 14px", borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
              {txError}
            </div>
          )}

          {txLoading && rows.length === 0 ? (
            <div className="text-center py-4">Loading...</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="display table table-bordered" style={{ width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ width: 34 }} />
                    <th>When</th>
                    <th>Kind</th>
                    <th>Status</th>
                    <th>Phone</th>
                    <th>Order</th>
                    <th>Amount</th>
                    <th>Points</th>
                    <th>Balance after</th>
                    <th>HTTP</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={TX_COLSPAN} style={{ textAlign: "center", padding: "40px 0" }}>
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24"
                            fill="none" stroke="var(--mly-text-muted)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                            <line x1="16" y1="13" x2="8" y2="13" />
                            <line x1="16" y1="17" x2="8" y2="17" />
                          </svg>
                          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--mly-text-muted)" }}>
                            {txBlocked ? "Call log unavailable" : "No Reelo calls logged"}
                          </span>
                          <span style={{ fontSize: 12, color: "var(--mly-text-muted)", maxWidth: 420, textAlign: "center" }}>
                            {txBlocked
                              ? txBlocked
                              : filtersActive
                                ? "Nothing matches these filters."
                                : "Every bill push, credit, debit, lookup and connection test lands here."}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    rows.map((r, i) => {
                      const id       = rowKey(r, i);
                      const kind     = String(pick(r, "kind") || "test").toLowerCase();
                      const st       = String(pick(r, "status") || "skipped").toLowerCase();
                      const kindSpec = KIND_CHIP[kind]   || KIND_CHIP.test;
                      const stSpec   = STATUS_CHIP[st]   || STATUS_CHIP.skipped;
                      const dir      = String(pick(r, "direction") || "");
                      /* `op` is the precise operation; `kind` is only the coarse bucket */
                      const op       = String(pick(r, "op") || "");
                      const http     = Number(pick(r, "httpStatus") || 0);
                      const err      = pick(r, "error");
                      /* An order is addressed by its NUMBER now. The row also
                         carries `order`/`orderId` (the internal row id), but
                         /admin/orders/{orderNo} matches order_no or legacy_id
                         only — linking by the row id 404s. */
                      const orderNo  = pick(r, "orderNumber");
                      /* PAISE — dividing here is the difference between ₹7.50 and ₹750 */
                      const amountPaise = Number(pick(r, "amountPaise") || 0);
                      const open     = expanded === id;
                      return (
                        <React.Fragment key={id}>
                          <tr onClick={() => setExpanded(open ? null : id)} style={{ cursor: "pointer" }}>
                            <td style={{ color: "var(--mly-text-soft)", textAlign: "center" }}><Caret open={open} /></td>
                            <td style={{ whiteSpace: "nowrap", fontSize: 12, color: "var(--mly-text-muted)" }}>
                              {formatDateTime(pick(r, "at"))}
                            </td>
                            <td>
                              <Chip spec={kindSpec} />
                              {op || dir ? (
                                <div style={{ fontSize: 10.5, color: "var(--mly-text-muted)", marginTop: 3 }}>
                                  {[op, dir].filter(Boolean).join(" · ")}
                                </div>
                              ) : null}
                            </td>
                            <td><Chip spec={stSpec} /></td>
                            <td style={{ whiteSpace: "nowrap" }}>{pick(r, "phone") || <span style={{ color: "var(--mly-text-muted)" }}>—</span>}</td>
                            <td style={{ whiteSpace: "nowrap" }} onClick={(e) => e.stopPropagation()}>
                              {orderNo ? (
                                <Link to={`/admin/orders/details/${encodeURIComponent(orderNo)}`}
                                  style={{ color: "var(--mly-heading)", fontWeight: 600, textDecoration: "underline" }}>
                                  {orderNo}
                                </Link>
                              ) : (<span style={{ color: "var(--mly-text-muted)" }}>—</span>)}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {amountPaise ? inr(amountPaise / 100) : <span style={{ color: "var(--mly-text-muted)" }}>—</span>}
                            </td>
                            <td style={{ whiteSpace: "nowrap", fontWeight: 700, color: "var(--mly-accent-2)" }}>
                              {Number(pick(r, "points") || 0) ? num(pick(r, "points")) : <span style={{ color: "var(--mly-text-muted)", fontWeight: 400 }}>—</span>}
                            </td>
                            <td style={{ whiteSpace: "nowrap", fontWeight: 700, color: "var(--mly-heading)" }}>
                              {showBalance(r?.balanceAfter)}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              <span style={{
                                fontSize: 11, fontWeight: 700, padding: "3px 9px", borderRadius: 8,
                                background: http >= 200 && http < 300 ? "#E9F7EF" : http ? "#FDECEA" : "#EFF1ED",
                                color: http >= 200 && http < 300 ? "#1E7A4B" : http ? "#C62828" : BRAND.stone,
                              }}>
                                {http || "—"}
                              </span>
                            </td>
                            <td style={{ maxWidth: 220, fontSize: 12, color: err ? "#C62828" : "var(--mly-text-muted)" }}>
                              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {err || (st === "skipped" ? "no-op — Reelo off or keyless" : "—")}
                              </div>
                            </td>
                          </tr>
                          {open && (
                            <tr>
                              <td colSpan={TX_COLSPAN} style={{ background: "var(--mly-surface-2)" }}>
                                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", padding: "6px 2px" }}>
                                  <div style={{ flex: "1 1 340px", minWidth: 280 }}>
                                    <div style={{ ...LABEL, marginBottom: 6 }}>Request</div>
                                    <pre style={PRE}>{pretty(pick(r, "request"))}</pre>
                                  </div>
                                  <div style={{ flex: "1 1 340px", minWidth: 280 }}>
                                    <div style={{ ...LABEL, marginBottom: 6 }}>Response</div>
                                    <pre style={PRE}>{pretty(pick(r, "response"))}</pre>
                                  </div>
                                </div>
                                {err ? (
                                  <div style={{
                                    margin: "10px 2px 4px", background: "#FDECEA", border: "1px solid #F5B7B1",
                                    borderRadius: 10, padding: "9px 14px", fontSize: 12.5, color: "#C62828",
                                  }}>
                                    <strong>Error:</strong> {String(err)}
                                  </div>
                                ) : null}
                                <div style={{ ...HINT, margin: "8px 2px 2px" }}>
                                  Audit row only — MilliyO does not derive a balance from these entries.
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* pagination */}
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 12, flexWrap: "wrap", marginTop: 12,
          }}>
            <span style={{ fontSize: 12, color: "var(--mly-text-muted)" }}>
              Page {page} · showing {rows.length} row{rows.length === 1 ? "" : "s"}
              {totalCount !== null ? ` of ${num(totalCount)}` : ""} · newest first
            </span>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={btn("ghost", page <= 1)}>Previous</button>
              <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}
                style={btn("ghost", !hasNext)}>Next</button>
            </div>
          </div>
        </CardShell>

      </div>
    </div>
  );
};

export default ReeloConsole;
